/**
 * Notes resource (CT-5528): one note, its comments, and the writes.
 *
 * The LIST of notes is not here: it rides the Activity tab call
 * (`quick_filter: 'notes'`, see `profileTabData.js`). This is the note itself.
 *
 * Real signatures and snake_case bodies; only the transport is stubbed. The
 * endpoints exist on the backend but their contract was not shared with the
 * frontend yet, so every path below is assumed and marked
 * `TODO(CT-5528 / backend)` — reconcile at cutover.
 *
 * Permission is the API's call and nothing else's: `can_edit` arrives on the
 * note, a private note the user may not read is simply absent from the list, and
 * a direct fetch of one answers 403. The frontend never derives any of it from a
 * role.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockNotesForTests } from './__mocks__/notes.mock.js';

/** The two things a note's scope can be. Who may read a private one is the backend's decision. */
export const NOTE_SCOPES = Object.freeze({ PUBLIC: 'public', PRIVATE: 'private' });

const mapAuthor = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') return { id: null, name: raw, role: null };
  return { id: raw.id ?? raw.user_id ?? null, name: raw.name ?? '', role: raw.role ?? null };
};

const mapLink = (raw = {}) => ({
  id: raw.id ?? raw.link_id ?? raw.url ?? '',
  label: raw.label ?? raw.name ?? raw.url ?? '',
  url: raw.url ?? '',
  kind: raw.kind ?? 'url',
});

/**
 * Map a raw note onto what the preview and the form render. Camel and snake
 * aliases are flattened with `??`, so a backend that settles on either needs no
 * change here. `canEdit` defaults to FALSE: an absent permission is not a grant.
 */
export const mapApiNoteToUi = (raw = {}) => ({
  id: raw.id ?? raw.note_id ?? raw.noteId ?? '',
  title: raw.title ?? '',
  body: raw.body ?? '',
  preview: raw.preview ?? null,
  summary: raw.ai_summary ?? raw.aiSummary ?? raw.summary ?? null,
  scope: raw.scope ?? NOTE_SCOPES.PUBLIC,
  createdAt: raw.created_at ?? raw.createdAt ?? raw.occurred_at ?? null,
  editedAt: raw.edited_at ?? raw.editedAt ?? null,
  author: mapAuthor(raw.author),
  canEdit: Boolean(raw.can_edit ?? raw.canEdit ?? false),
  links: (raw.links ?? []).map(mapLink),
});

export const mapApiNoteCommentToUi = (raw = {}) => ({
  id: raw.id ?? raw.comment_id ?? raw.commentId ?? '',
  text: raw.text ?? raw.body ?? '',
  createdAt: raw.created_at ?? raw.createdAt ?? null,
  author: mapAuthor(raw.author),
});

/** TODO(CT-5528 / backend): `POST /v0/profile/notes/get` with `{ note_id }`. */
export const getNote = async ({ noteId } = {}) => {
  const response = await request('/v0/profile/notes/get', { note_id: noteId });
  return validateApiResponse(response, 'Failed to load the note');
};

/**
 * 422 for a missing title or body, 403 when the caller may not write here.
 *
 * TODO(CT-5528 / backend): `POST /v0/profile/notes/create`.
 */
export const createNote = async ({
  entityType,
  entityId,
  title,
  body,
  scope = NOTE_SCOPES.PUBLIC,
  attachments = [],
  urls = [],
} = {}) => {
  const response = await request('/v0/profile/notes/create', {
    entity_type: entityType,
    entity_id: entityId,
    title,
    body,
    scope,
    attachments,
    urls,
  });
  return validateApiResponse(response, 'Failed to create the note');
};

/** 403 when the caller is not the author. TODO(CT-5528 / backend): `POST /v0/profile/notes/update`. */
export const updateNote = async ({ noteId, title, body, scope, attachments = [], urls = [] } = {}) => {
  const response = await request('/v0/profile/notes/update', {
    note_id: noteId,
    title,
    body,
    scope,
    attachments,
    urls,
  });
  return validateApiResponse(response, 'Failed to save the note');
};

/** TODO(CT-5528 / backend): `POST /v0/profile/notes/comments/list`. */
export const listNoteComments = async ({ noteId, page = 1, pageSize = 10 } = {}) => {
  const response = await request('/v0/profile/notes/comments/list', {
    note_id: noteId,
    page,
    page_size: pageSize,
  });
  return validateApiResponse(response, 'Failed to load the comments');
};

/** TODO(CT-5528 / backend): `POST /v0/profile/notes/comments/create`. */
export const createNoteComment = async ({ noteId, text } = {}) => {
  const response = await request('/v0/profile/notes/comments/create', { note_id: noteId, text });
  return validateApiResponse(response, 'Failed to add the comment');
};
